(ns common.resource.query.decision-tree
  (:require [cljs-bean.core :refer [->clj ->js]]
            [clojure.set :as set]
            [common.queries :refer [invalidate-queries
                                    prevent-client-error-retries
                                    query-client]]
            [common.queries.http :as qhttp]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.navigation :as navigation]
            [promesa.core :as p]
            [re-frame.core :as rf]))

(defn add-toaster [message-details]
  (rf/dispatch [:toaster-bus/add (event/get-decision-tree-toaster-dispatch message-details)]))

;; Fetch all the trees
(defn fetch-decision-trees-query
  [{:keys [advertiser-id adset-id]}]
  #js {:queryKey #js ["decision-trees" advertiser-id adset-id]
       :enabled (and (some? advertiser-id) (some? adset-id))
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset-decision-trees
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id})))
       ;; 1 minute (60 seconds)
       :staleTime (* 60 1000)})

(defn is-decisision-tree-processing [^js decision-tree-data]
  (= (-> decision-tree-data ->clj :data :activation-status :state)
     "processing"))

;; Fetch one tree
(defn fetch-decision-tree-query
  [{:keys [advertiser-id adset-id decision-tree-id] :as params}]
  #js {:enabled (and (some? decision-tree-id) (some? advertiser-id) (some? adset-id))
       :staleTime (* 60 1000) ; 1 minute
       :select identity
       :retry prevent-client-error-retries
       :queryKey #js ["decision-tree" advertiser-id adset-id decision-tree-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset-decision-tree params)))
       :refetchInterval (fn [^js query]
                          (when (is-decisision-tree-processing (.. query -state -data))
                            1000))})

;; --- Mutations

(def ^:private newly-created-decision-tree-data
  {})

(defn get-decision-tree-mutation-success-handler
  [{:keys [advertiser-id adset-id on-success]
    :or {on-success identity}}]
  (fn []
    (invalidate-queries {:queryKey ["decision-trees" advertiser-id adset-id]})
    (on-success)))

(defn create-new-decision-tree-mutation
  [{:keys [advertiser-id adset-id] :as params}]
  #js {:mutationKey #js ["create-new-decision-tree" advertiser-id adset-id]
       :mutationFn (fn [_variables]
                     (qhttp/post (qhttp/render :api/content-build-adset-decision-trees
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id})
                                 {:body newly-created-decision-tree-data}))
       :onSuccess (get-decision-tree-mutation-success-handler
                   (assoc params :on-success navigation/enter-editor-mode))})

(defn publish-decision-tree-mutation
  [{:keys [advertiser-id adset-id decision-tree-id]}]
  #js {:mutationKey #js ["publish-decision-tree" advertiser-id adset-id decision-tree-id]
       :mutationFn (fn [_variables]
                     (rf/dispatch [:loader/open])
                     (qhttp/patch (qhttp/render :api/content-build-adset-decision-tree
                                                {:advertiser-id advertiser-id
                                                 :adset-id adset-id
                                                 :decision-tree-id decision-tree-id})
                                  {:body {:status "published"}}))
       :onError (fn [response]
                  (let [error (-> response :data .-error)
                        error-message (-> error .-message)]
                    (rf/dispatch
                     [:modal/open
                      {:id :manage.adset-decision-tree-page.view.core/publishing-error-modal
                       :props {:error-message error-message}}])))
       :onSuccess
       (fn [{{{:keys [activation-status]} :data} :data}]
         (let [query-key ["decision-tree" advertiser-id adset-id decision-tree-id]]
           (.setQueryData
            query-client
            (->js query-key)
            (fn [old-data]
              (when old-data
                (-> (js->clj old-data :keywordize-keys true)
                    (assoc-in [:data :data :activation-status] activation-status)
                    ->js))))
           (navigation/exit-to-table)
           (invalidate-queries {:queryKey query-key})
           (invalidate-queries {:queryKey ["decision-trees" advertiser-id adset-id]})
           (invalidate-queries {:queryKey ["adset-activation" advertiser-id adset-id]})))
       :onSettled #(rf/dispatch [:loader/close])})

(defn create-draft-decision-tree-mutation
  [{:keys [advertiser-id adset-id decision-tree-id on-success]}]
  #js {:mutationKey #js ["duplicate-decision-tree" advertiser-id adset-id decision-tree-id]
       :mutationFn (fn [_variables]
                     (qhttp/post (qhttp/render :api/content-build-adset-decision-tree-duplicate
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id
                                                :decision-tree-id decision-tree-id})))
       :onSuccess
       (fn [^js response]
         (let [{new-draft-decision-tree :data} (->clj response)
               decision-trees-query-key ["decision-trees" advertiser-id adset-id]]
           (.setQueryData
            query-client
            (->js decision-trees-query-key)
            (fn [old-data]
              (when old-data
                (->js
                 {:data (-> (js->clj old-data :keywordize-keys true)
                            :data
                            (conj (dissoc new-draft-decision-tree :nodes)))}))))
           ;; Need to invalidate here as well, as `new-draft-decision-tree` doesn't
           ;; contain updated-by-user.
           (invalidate-queries {:queryKey ["decision-trees" advertiser-id adset-id]})
           (invalidate-queries {:queryKey ["adset-activation" advertiser-id adset-id]})
           (on-success)))})

(defn discard-draft-decision-tree-mutation
  [{:keys [advertiser-id adset-id decision-tree-id] :as params}]
  #js {:mutationKey #js ["discard-decision-tree" advertiser-id adset-id decision-tree-id]
       :mutationFn (fn [_variables]
                     (qhttp/delete (qhttp/render :api/content-build-adset-decision-tree
                                                 {:advertiser-id advertiser-id
                                                  :adset-id adset-id
                                                  :decision-tree-id decision-tree-id})))
       :onSuccess (get-decision-tree-mutation-success-handler params)})

(defn upgrade-all-variants-mutation
  [{:keys [advertiser-id adset-id decision-tree-id]}]
  #js {:mutationKey #js ["upgrade-all-variants" advertiser-id adset-id decision-tree-id]
       :mutationFn (fn [_variables]
                     (rf/dispatch [:loader/open])
                     (qhttp/post (qhttp/render :api/content-build-adset-decision-tree-upgrade-all-variants
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id
                                                :decision-tree-id decision-tree-id})))
       :onSuccess (fn []
                    (invalidate-queries
                     {:queryKey ["decision-tree" advertiser-id adset-id decision-tree-id]})
                    (add-toaster {:id ::all-variants-upgraded
                                  :message "You have updated your Ad variants"}))
       :onSettled #(rf/dispatch [:loader/close])})

(defn remove-archived-ad-variants-mutation
  [{:keys [advertiser-id adset-id decision-tree-id]}]
  #js {:mutationKey #js ["remove-archived-ad-variants" advertiser-id adset-id decision-tree-id]
       :mutationFn (fn [_variables]
                     (rf/dispatch [:loader/open])
                     (qhttp/post (qhttp/render :api/content-build-adset-decision-tree-remove-archived-ad-variants
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id
                                                :decision-tree-id decision-tree-id})))
       :onSuccess (fn []
                    (invalidate-queries
                     {:queryKey ["decision-tree" advertiser-id adset-id decision-tree-id]})
                    (add-toaster {:id ::archived-variants-removed
                                  :message "Archived ad variants removed from the tree"}))
       :onSettled #(rf/dispatch [:loader/close])})

(defn- meta-adsets-options [^js data]
  (map #(set/rename-keys % {:name :description}) (->clj data)))

(defn fetch-meta-adsets-query [{:keys [advertiser-id adset-id]}]
  #js {:queryKey #js ["meta-adsets" advertiser-id adset-id]
       :enabled (and (some? advertiser-id) (some? adset-id))
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset-meta-adsets
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id})))
       :staleTime (* 5 60 1000)
       :retry prevent-client-error-retries
       :select meta-adsets-options})

(defn- tiktok-ad-group-options [^js data]
  (map #(set/rename-keys % {:ad-group-name :description}) (->clj data)))

(defn fetch-tiktok-ad-groups-query [{:keys [advertiser-id adset-id]}]
  #js {:queryKey #js ["tiktok-ad-groups" advertiser-id adset-id]
       :enabled (and (some? advertiser-id) (some? adset-id))
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset-tiktok-ad-groups
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id})))
       :staleTime (* 5 60 1000)
       :select tiktok-ad-group-options})

(defn targeting-config-query [{:keys [advertiser-id adset-id]}]
  #js {:queryKey #js ["targeting-config" advertiser-id adset-id]
       :enabled (and (some? advertiser-id) (some? adset-id))
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset-targeting-config
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id})))
       :retry prevent-client-error-retries
       :staleTime (* 5 60 1000)})

(defn targeting-config-mutation
  [{:keys [advertiser-id adset-id] :as url-context}]
  #js {:mutationKey #js ["targeting-config" advertiser-id adset-id]
       :enabled (and (some? advertiser-id) (some? adset-id))
       :onMutate #(rf/dispatch [:loader/open])
       :onError (fn [{:keys [data]}]
                  (rf/dispatch [:response-handler/handle-response
                                {:type :error
                                 :response (->clj data)}]))
       :onSettled (fn []
                    (invalidate-queries
                     {:queryKey ["targeting-config" advertiser-id adset-id]})
                    (invalidate-queries
                     {:queryKey ["decision-trees" advertiser-id adset-id]})
                    (rf/dispatch [:loader/close]))
       :mutationFn
       (fn [{:keys [targeting-config is-update draft-id]}]
         (p/let [_targeting-config-response
                 ((if is-update
                    qhttp/put
                    qhttp/post) (qhttp/render :api/content-build-adset-targeting-config
                                              url-context)
                                {:body targeting-config})
                 _discard-draft-reponse
                 (when (some? draft-id)
                   (qhttp/delete (qhttp/render :api/content-build-adset-decision-tree
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id
                                                :decision-tree-id draft-id})))]
           (qhttp/post
            (qhttp/render :api/content-build-adset-decistion-tree-generate url-context))))})

(defn start-aco-mutation
  [{:keys [advertiser-id adset-id]}]
  #js {:mutationKey #js ["aco-start" advertiser-id adset-id]
       :mutationFn (fn []
                     (qhttp/post (qhttp/render :api/content-build-adset-decision-trees-aco-start
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id})))
       :onSuccess (fn []
                    (invalidate-queries
                     {:queryKey ["adset-aco-active-configuration" advertiser-id adset-id]})
                    (add-toaster
                     {:id ::enabled-aco
                      :message "Enabled creative optimization"}))})

(defn pause-aco-mutation
  [{:keys [advertiser-id adset-id configuration-id]}]
  #js {:mutationKey #js ["aco-pause" advertiser-id adset-id configuration-id]
       :mutationFn (fn []
                     (qhttp/post (qhttp/render :api/adset-aco-configuration-pause
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id
                                                :configuration-id configuration-id})))
       :onSuccess (fn []
                    (invalidate-queries
                     {:queryKey ["adset-aco-active-configuration" advertiser-id adset-id]})
                    (add-toaster
                     {:id ::disabled-aco
                      :message "Disabled creative optimization"}))})
